import { supabase } from '../lib/supabase';

// Map profiles table snake_case → camelCase for components
const mapProfile = (p) => ({
  id: p.id,
  email: p.email,
  firstName: p.first_name || '',
  lastName: p.last_name || '',
  phone: p.phone || '',
  role: p.role,
  specialization: p.specialization || '',
  bio: p.bio || '',
  isActive: p.is_active ?? true,
  coachId: p.coach_id || null,
  createdAt: p.created_at,
});

// Map doctors table (with joined user profile) → camelCase for components
const mapDoctor = (d) => ({
  id: d.id,
  userId: d.user_id,
  // Flattened from joined user:user_id(...)
  firstName: d.user?.first_name || '',
  lastName: d.user?.last_name || '',
  email: d.user?.email || '',
  phone: d.user?.phone || '',
  // Doctor-specific fields
  licenseNumber: d.license_number || '',
  licenseState: d.license_state || '',
  licenseExpiry: d.license_expiry || '',
  registrationNumber: d.registration_number || '',
  specialization: d.specialization || '',
  qualifications: d.qualifications || '',
  yearsExperience: d.years_experience || '',
  bio: d.bio || '',
  consultationFee: d.consultation_fee || '',
  commissionPercentage: d.commission_percentage || '',
  isVerified: d.is_verified ?? false,
  isActive: d.is_active ?? true,
  verifiedAt: d.verified_at,
  verificationNotes: d.verification_notes || '',
  createdAt: d.created_at,
});

export const adminAPI = {
  // ==================== Coaches ====================

  getAllCoaches: async () => {
    const { data, error } = await supabase
      .from('profiles').select('*').eq('role', 'COACH').order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapProfile);
  },

  getCoachById: async (coachId) => {
    const { data, error } = await supabase
      .from('profiles').select('*').eq('id', coachId).single();
    if (error) throw error;
    return mapProfile(data);
  },

  getCoachClients: async (coachId) => {
    const { data, error } = await supabase
      .from('profiles').select('*').eq('coach_id', coachId).eq('role', 'PATIENT');
    if (error) throw error;
    return (data ?? []).map(mapProfile);
  },

  // Assign COACH role to an existing registered user (found by email).
  // The person must have already registered a Supabase account first.
  createCoach: async (coachData) => {
    // Step 1: find the existing profile by email
    const { data: existing, error: findErr } = await supabase
      .from('profiles')
      .select('id')
      .eq('email', coachData.email)
      .maybeSingle();
    if (findErr) throw findErr;
    if (!existing) {
      throw new Error(
        `No account found for "${coachData.email}". The person must register first at /register, then you can assign them the Coach role here.`
      );
    }

    // Step 2: promote to COACH and update profile fields
    const updates = {
      role: 'COACH',
      specialization: coachData.specialization || null,
      bio: coachData.bio || null,
    };
    if (coachData.phone) updates.phone = coachData.phone;

    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', existing.id)
      .select()
      .single();
    if (error) throw error;
    return mapProfile(data);
  },

  updateCoach: async (coachId, coachData) => {
    const updates = {
      specialization: coachData.specialization,
      bio: coachData.bio,
    };
    if (coachData.firstName !== undefined) updates.first_name = coachData.firstName;
    if (coachData.lastName !== undefined) updates.last_name = coachData.lastName;
    if (coachData.phone !== undefined) updates.phone = coachData.phone;
    if (coachData.isActive !== undefined) updates.is_active = coachData.isActive;

    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', coachId).select().single();
    if (error) throw error;
    return mapProfile(data);
  },

  deleteCoach: async (coachId) => {
    // Soft delete — deactivate
    const { error } = await supabase
      .from('profiles').update({ is_active: false }).eq('id', coachId);
    if (error) throw error;
    return { message: 'Coach deactivated' };
  },

  // ==================== Clients ====================

  getAllClients: async (filters = {}) => {
    let query = supabase
      .from('profiles')
      .select('*, coach:coach_id(first_name, last_name), subscriptions(plan_name, plan_id, status)')
      .eq('role', 'PATIENT').order('created_at', { ascending: false });

    if (filters.coachId === 'unassigned') {
      query = query.is('coach_id', null);
    } else if (filters.coachId) {
      query = query.eq('coach_id', filters.coachId);
    }
    if (filters.search) {
      query = query.or(`first_name.ilike.%${filters.search}%,last_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []).map((p) => {
      const activeSub = p.subscriptions?.find(s => s.status === 'ACTIVE') || p.subscriptions?.[0] || null;
      return {
        ...mapProfile(p),
        coach: p.coach
          ? { firstName: p.coach.first_name || '', lastName: p.coach.last_name || '' }
          : null,
        planId: activeSub?.plan_id || null,
        planName: activeSub?.plan_name || null,
        subscriptionStatus: activeSub?.status || null,
      };
    });
  },

  getClientById: async (clientId) => {
    const { data, error } = await supabase
      .from('profiles').select('*').eq('id', clientId).single();
    if (error) throw error;
    return mapProfile(data);
  },

  assignCoachToClient: async (clientId, coachId) => {
    const { data, error } = await supabase
      .from('profiles').update({ coach_id: coachId }).eq('id', clientId).select().single();
    if (error) throw error;
    return mapProfile(data);
  },

  // ==================== Admin Dashboard ====================

  getActiveClients: async () => {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('user_id, plan_name, end_date, profiles!inner(first_name, last_name, email)')
      .eq('status', 'ACTIVE');
    if (error) throw error;
    return data ?? [];
  },

  // ==================== Plan Documents ====================

  uploadDocument: async (file, planType, documentType, userId = null) => {
    const { data: { user } } = await supabase.auth.getUser();
    const fileName = `${planType}/${documentType}/${Date.now()}_${file.name}`;

    const { error: uploadError } = await supabase.storage
      .from('plan-documents')
      .upload(fileName, file, { contentType: 'application/pdf' });
    if (uploadError) throw uploadError;

    const { data, error } = await supabase
      .from('plan_documents')
      .insert({
        plan_type: planType,
        document_type: documentType,
        file_name: file.name,
        storage_path: fileName,
        file_size: file.size,
        uploaded_by: user.id,
        assigned_to: userId ?? null,
      })
      .select().single();
    if (error) throw error;
    return data;
  },

  getAllDocuments: async () => {
    const { data, error } = await supabase
      .from('plan_documents').select('*').order('created_at', { ascending: false });
    if (error) throw error;
    return data ?? [];
  },

  deleteDocument: async (documentId) => {
    const { data: doc } = await supabase
      .from('plan_documents').select('storage_path').eq('id', documentId).single();
    if (doc?.storage_path) {
      await supabase.storage.from('plan-documents').remove([doc.storage_path]);
    }
    await supabase.from('plan_documents').delete().eq('id', documentId);
    return { message: 'Document deleted' };
  },

  // ==================== Doctors ====================

  getAllDoctors: async (filters = {}) => {
    let query = supabase
      .from('doctors')
      .select('*, user:user_id(first_name, last_name, email, phone)')
      .order('created_at', { ascending: false });
    if (filters.verified !== undefined && filters.verified !== '') {
      query = query.eq('is_verified', filters.verified === 'true');
    }
    if (filters.specialization) query = query.eq('specialization', filters.specialization);
    if (filters.search) {
      query = query.ilike('specialization', `%${filters.search}%`);
    }
    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []).map(mapDoctor);
  },

  getDoctorById: async (doctorId) => {
    const { data, error } = await supabase
      .from('doctors').select('*, user:user_id(first_name, last_name, email, phone)')
      .eq('id', doctorId).single();
    if (error) throw error;
    return mapDoctor(data);
  },

  getDoctorClients: async (doctorId) => {
    const { data, error } = await supabase
      .from('doctor_client_assignments')
      .select('*, client:client_id(*)')
      .eq('doctor_id', doctorId).eq('status', 'ACTIVE');
    if (error) throw error;
    return (data ?? []).map((a) => mapProfile(a.client));
  },

  // Assign DOCTOR role to an existing registered user (found by email),
  // then create their doctors table record with license info.
  createDoctor: async (doctorData) => {
    // Step 1: find existing profile by email
    const { data: profile, error: findErr } = await supabase
      .from('profiles')
      .select('id')
      .eq('email', doctorData.email)
      .maybeSingle();
    if (findErr) throw findErr;
    if (!profile) {
      throw new Error(
        `No account found for "${doctorData.email}". The doctor must register first at /register, then you can add them here.`
      );
    }

    // Step 2: promote profile to DOCTOR role
    await supabase.from('profiles').update({ role: 'DOCTOR' }).eq('id', profile.id);

    // Step 3: insert into doctors table
    const { data, error } = await supabase
      .from('doctors')
      .insert({
        user_id: profile.id,
        license_number: doctorData.licenseNumber,
        license_state: doctorData.licenseState || null,
        license_expiry: doctorData.licenseExpiry || null,
        registration_number: doctorData.registrationNumber || null,
        specialization: doctorData.specialization || null,
        qualifications: doctorData.qualifications || null,
        years_experience: doctorData.yearsExperience ? parseInt(doctorData.yearsExperience) : null,
        bio: doctorData.bio || null,
        consultation_fee: doctorData.consultationFee ? parseFloat(doctorData.consultationFee) : null,
        commission_percentage: doctorData.commissionPercentage ? parseFloat(doctorData.commissionPercentage) : null,
      })
      .select('*, user:user_id(first_name, last_name, email, phone)')
      .single();
    if (error) throw error;
    return mapDoctor(data);
  },

  updateDoctor: async (doctorId, doctorData) => {
    const updates = {
      specialization: doctorData.specialization,
      qualifications: doctorData.qualifications,
      bio: doctorData.bio,
    };
    if (doctorData.licenseNumber !== undefined) updates.license_number = doctorData.licenseNumber;
    if (doctorData.licenseState !== undefined) updates.license_state = doctorData.licenseState;
    if (doctorData.licenseExpiry !== undefined) updates.license_expiry = doctorData.licenseExpiry;
    if (doctorData.registrationNumber !== undefined) updates.registration_number = doctorData.registrationNumber;
    if (doctorData.yearsExperience !== undefined) updates.years_experience = doctorData.yearsExperience ? parseInt(doctorData.yearsExperience) : null;
    if (doctorData.consultationFee !== undefined) updates.consultation_fee = doctorData.consultationFee ? parseFloat(doctorData.consultationFee) : null;
    if (doctorData.commissionPercentage !== undefined) updates.commission_percentage = doctorData.commissionPercentage ? parseFloat(doctorData.commissionPercentage) : null;
    if (doctorData.isActive !== undefined) updates.is_active = doctorData.isActive;

    const { data, error } = await supabase
      .from('doctors')
      .update(updates)
      .eq('id', doctorId)
      .select('*, user:user_id(first_name, last_name, email, phone)')
      .single();
    if (error) throw error;
    return mapDoctor(data);
  },

  deleteDoctor: async (doctorId) => {
    const { error } = await supabase
      .from('doctors').update({ is_active: false }).eq('id', doctorId);
    if (error) throw error;
    return { message: 'Doctor deactivated' };
  },

  // isVerified: boolean, notes: optional string
  verifyDoctor: async (doctorId, { isVerified, notes }) => {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('doctors')
      .update({
        is_verified: isVerified,
        verified_at: isVerified ? new Date().toISOString() : null,
        verified_by: isVerified ? user.id : null,
        verification_notes: notes || null,
      })
      .eq('id', doctorId)
      .select('*, user:user_id(first_name, last_name, email, phone)')
      .single();
    if (error) throw error;
    return mapDoctor(data);
  },

  getUnassignedDoctorClients: async (filters = {}) => {
    let query = supabase
      .from('profiles').select('*')
      .eq('role', 'PATIENT')
      .not('id', 'in', `(SELECT client_id FROM doctor_client_assignments WHERE status='ACTIVE')`);
    if (filters.search) {
      query = query.or(`first_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
    }
    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []).map(mapProfile);
  },

  assignDoctorToClient: async (clientId, doctorId) => {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('doctor_client_assignments')
      .insert({ doctor_id: doctorId, client_id: clientId, assigned_by: user.id })
      .select().single();
    if (error) throw error;
    return data;
  },

  transferClientDoctor: async ({ clientId, fromDoctorId, toDoctorId, reason }) => {
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('doctor_client_assignments')
      .update({ status: 'TRANSFERRED', transferred_to_doctor: toDoctorId, transfer_reason: reason, transferred_at: new Date().toISOString() })
      .eq('doctor_id', fromDoctorId).eq('client_id', clientId).eq('status', 'ACTIVE');
    const { data, error } = await supabase
      .from('doctor_client_assignments')
      .insert({ doctor_id: toDoctorId, client_id: clientId, assigned_by: user.id })
      .select().single();
    if (error) throw error;
    return data;
  },
};

export default adminAPI;
